// WS8 — Servicing platform card on the loan Overview tab. Shows the
// currently assigned TPS and (gated Loans.Manage) offers a Reassign modal.
// SP-3: the seeded default TPS reads as "No servicer assigned" (never
// "Unnamed entity"), the picker annotates/disables unready servicers up
// front, and a rejected reassign surfaces the server's plain-language
// message with a link to the TPS setup checklist.

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Card, FormField } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import { extractErrorMessage } from "@/api/client";
import {
  useReassignLoanTps,
  useTps,
  useTpsList,
  useTpsReadiness,
} from "@/hooks/portfolioAdmin/useTps";
import { useEntityNameMap } from "@/hooks/capitalMarkets/useCounterpartyNames";
import { NO_SERVICER_ASSIGNED, tpsDisplayName } from "@/lib/tpsDisplay";
import {
  buildReadinessMap,
  tpsOptionDisabled,
  tpsOptionLabel,
} from "@/lib/tpsPicker";
import type { LoanSummaryDto } from "@/api/portfolioAdmin/types";
import type { TpsDto } from "@/types/tps";

export function TpsAssignmentCard({ loan }: { loan: LoanSummaryDto }) {
  const { hasPermission } = useAuth();
  const canReassign = hasPermission("Loans.Manage");
  const tpsId: string | null = loan.tpsId ?? null;

  const tpsQuery = useTps(tpsId ?? undefined);
  const tps = tpsQuery.data;

  // Resolve the linked-entity legal name for display via the shared name map
  // (used by every list/picker in the same surface — keeps the cache hot).
  const { map: entityNames } = useEntityNameMap(!!tps?.entityId);

  const [showReassign, setShowReassign] = useState(false);

  const displayName = useMemo(() => {
    // No assignment and the default placeholder are the same concept.
    if (!tpsId) return NO_SERVICER_ASSIGNED;
    if (tps) return tpsDisplayName(tps, entityNames.get(tps.entityId));
    return "Loading…";
  }, [tpsId, tps, entityNames]);

  return (
    <Card data-testid="tps-assignment-card">
      <h3 className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow m-0 mb-space-1">
        Servicing Platform
      </h3>
      <div className="flex items-center justify-between gap-space-2">
        <div className="text-small text-onyx">
          TPS:{" "}
          <span className="font-semibold" data-testid="tps-assigned-name">
            {displayName}
          </span>
        </div>
        {canReassign && (
          <Button
            size="sm"
            variant="text"
            onClick={() => setShowReassign(true)}
            data-testid="tps-reassign-trigger"
          >
            Reassign
          </Button>
        )}
      </div>

      {showReassign && (
        <ReassignTpsModal
          loanId={loan.id}
          currentTpsId={tpsId}
          onClose={() => setShowReassign(false)}
        />
      )}
    </Card>
  );
}

function ReassignTpsModal({
  loanId,
  currentTpsId,
  onClose,
}: {
  loanId: string;
  currentTpsId: string | null;
  onClose: () => void;
}) {
  const toast = useToast();
  const reassign = useReassignLoanTps(loanId);
  const listQuery = useTpsList({ status: "Active", pageSize: 200 });
  const options = useMemo<TpsDto[]>(
    () => listQuery.data?.data ?? [],
    [listQuery.data],
  );
  const { map: entityNames } = useEntityNameMap();

  const readinessQuery = useTpsReadiness();
  const readiness = useMemo(
    () => buildReadinessMap(readinessQuery.data),
    [readinessQuery.data],
  );

  const [tpsId, setTpsId] = useState<string>(currentTpsId ?? "");
  const [submitError, setSubmitError] = useState<string | null>(null);

  const selected = options.find((p) => p.id === tpsId);
  const selectedReadiness = tpsId ? readiness.get(tpsId) : undefined;
  const selectedUnready =
    !!selected && !selected.isDefault && selectedReadiness?.isReady === false;

  const handleSubmit = () => {
    if (!tpsId) {
      toast.error("Select a TPS provider first.");
      return;
    }
    setSubmitError(null);
    reassign
      .mutateAsync({ tpsId })
      .then(() => {
        toast.success("TPS assignment updated.");
        onClose();
      })
      .catch((err) => setSubmitError(extractErrorMessage(err)));
  };

  return (
    <Modal
      open
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="Reassign TPS"
      description="Choose a new Third-Party Servicer for this loan."
      size="sm"
      footer={
        <div className="flex items-center justify-end gap-space-2">
          <Button variant="text" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            loading={reassign.isPending}
            onClick={handleSubmit}
            data-testid="tps-reassign-submit"
          >
            Reassign
          </Button>
        </div>
      }
    >
      <FormField label="TPS Provider" required>
        <Select
          value={tpsId}
          onChange={(e) => {
            setTpsId(e.target.value);
            setSubmitError(null);
          }}
          aria-label="TPS Provider"
        >
          <option value="">Select a TPS…</option>
          {options.map((p) => (
            <option
              key={p.id}
              value={p.id}
              disabled={tpsOptionDisabled(p, readiness)}
            >
              {p.id === currentTpsId ? "(current) " : ""}
              {tpsOptionLabel(p, entityNames.get(p.entityId), readiness)}
            </option>
          ))}
        </Select>
      </FormField>
      {selected?.isDefault && (
        <p
          className="text-small text-gray-mid m-0 mt-space-2"
          data-testid="tps-unassign-note"
        >
          This removes the servicer from the loan — billing pauses until a new
          TPS is assigned.
        </p>
      )}
      {selectedUnready && (
        <p
          className="text-small text-gray-mid m-0 mt-space-2"
          data-testid="tps-not-ready-note"
        >
          {selectedReadiness?.missingSteps[0] ??
            "This servicer hasn't finished its setup checklist."}{" "}
          <Link
            to={`/portfolio-admin/tps/${tpsId}?tab=overview`}
            className="text-gold-shadow underline"
          >
            Open setup checklist
          </Link>
        </p>
      )}
      {submitError && (
        <p
          className="text-small text-status-error m-0 mt-space-2"
          role="alert"
          data-testid="tps-reassign-error"
        >
          {submitError}
          {selected && !selected.isDefault && (
            <>
              {" "}
              <Link
                to={`/portfolio-admin/tps/${selected.id}`}
                className="text-gold-shadow underline"
              >
                Open TPS setup
              </Link>
            </>
          )}
        </p>
      )}
    </Modal>
  );
}
